import {
  BodyText,
  Box,
  Card,
  Flex,
  Heading,
  Modal,
  ModalContent,
  ModalRoot,
  ModalTrigger,
  Skeleton,
  SkeletonBox,
  Strong,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tabs,
  TabsList,
  UnstyledIconButton,
} from '@utilitywarehouse/hearth-react';
import { InfoMediumIcon } from '@utilitywarehouse/hearth-react-icons';
import clsx from 'clsx';

import { EnergyFuelType, EnergyQuote } from '@generated/gql/graphql';
import { useSyncState } from '@hooks/useSyncState';
import { formatCurrency } from '@utils/format';

import { hasServices } from '../utils';
import './styles.scss';

const TARIFF_DESCRIPTIONS = {
  variable:
    'Government changes the prices every 3 months, but we always give you a discount',
  tracker:
    'Like Variable, but with a bigger discount in exchange for a 12 month contract',
  fixed:
    'Prices are locked for 12 months, making you immune to Government price changes',
};

const BEST_FOR = {
  variable: 'Guaranteed savings and the freedom to leave',
  tracker: 'Maximum savings over Variable',
  fixed: 'Price certainty',
};

const ENERGY_TARIFFS: {
  key: 'variable' | 'tracker' | 'fixed';
  label: string;
}[] = [
  { key: 'variable', label: 'Variable' },
  { key: 'tracker', label: 'Tracker' },
  { key: 'fixed', label: 'Fixed' },
];

const INFO_MODAL = {
  cost: {
    heading: 'Cost estimate',
    body: 'This is an annual estimate, based on your estimated usage and the current Price Cap rates (which will change). What you actually pay will vary.',
  },
  popularity: {
    heading: 'Popularity data',
    body: 'Based on new customers who signed up in the last 7 days.',
  },
};

const calcQuoteAnnualPounds = (
  quote: EnergyQuote,
  hasElec: boolean,
  hasGas: boolean
): number => {
  let total = 0;
  if (hasElec && quote.electricity)
    total += parseFloat(quote.electricity.annualCost.value);
  if (hasGas && quote.gas) total += parseFloat(quote.gas.annualCost.value);
  if (total === 0 && quote.annualEstimateCost)
    return parseFloat(quote.annualEstimateCost.value);
  return total;
};

const CostLabel = ({ onInfoClick }: { onInfoClick: () => void }) => (
  <Flex alignItems="center" spacing="xs">
    <Strong>
      Cost <span style={{ fontWeight: 'normal' }}>(est)</span>
    </Strong>
    <ModalTrigger>
      <UnstyledIconButton
        label="About cost estimate"
        size="sm"
        type="button"
        onClick={onInfoClick}
      >
        <InfoMediumIcon
          width={14}
          height={14}
          className="tariffComp__infoIcon"
        />
      </UnstyledIconButton>
    </ModalTrigger>
  </Flex>
);

interface TariffComparisonProps {
  variable: EnergyQuote;
  tracker: Nullable<EnergyQuote>;
  fixed: Nullable<EnergyQuote>;
  fuelType: EnergyFuelType;
  isLoading?: boolean;
}

const TariffComparison = ({
  variable,
  tracker,
  fixed,
  fuelType,
  isLoading,
}: TariffComparisonProps) => {
  const [activeTariff, setActiveTariff] = useSyncState<
    'variable' | 'tracker' | 'fixed'
  >('tariff-active', 'variable');
  const [activeInfo, setActiveInfo] = useSyncState<
    'cost' | 'popularity' | null
  >('tariff-info', null);

  if (isLoading) {
    return (
      <Box className="tariffComp">
        <Skeleton loadingTitle="Tariff comparison loading">
          <Flex direction="column" spacing="sm">
            <SkeletonBox borderRadius="md" height="32px" width="280px" />
            <SkeletonBox borderRadius="lg" height="320px" width="100%" />
          </Flex>
        </Skeleton>
      </Box>
    );
  }

  const { hasElectricity, hasGas } = hasServices(fuelType);
  const variablePrice = Math.round(
    calcQuoteAnnualPounds(variable, hasElectricity, hasGas)
  );
  const trackerPrice = tracker
    ? Math.round(calcQuoteAnnualPounds(tracker, hasElectricity, hasGas))
    : null;
  const fixedPrice = fixed
    ? Math.round(calcQuoteAnnualPounds(fixed, hasElectricity, hasGas))
    : null;

  const tabPrice = {
    variable: variablePrice,
    tracker: trackerPrice,
    fixed: fixedPrice,
  }[activeTariff];
  // const tabSignups = `${stats[activeTariff]} of people choose ${ENERGY_TARIFFS.find((t) => t.key === activeTariff)!.label}`;

  const costLabel = <CostLabel onInfoClick={() => setActiveInfo('cost')} />;
  // const popularityLabel = <PopularityLabel onInfoClick={() => setActiveInfo('popularity')} />;

  return (
    <ModalRoot
      open={activeInfo !== null}
      onOpenChange={(open) => !open && setActiveInfo(null)}
    >
      <Box className="tariffComp">
        <Heading as="h2" size="lg" marginBottom="200">
          Which Energy tariff is right for me?
        </Heading>
        <Card
          direction="column"
          spacing="none"
          paddingNone
          colorScheme="neutralStrong"
          variant="subtle"
          className="tariffComp__card"
        >
          {/* ── Wide layout (table) ── */}
          <div className="tariffComp__tableLayout">
            <Table>
              <TableHeader className="tariffComp__tableHeader">
                <TableHeaderCell
                  className={clsx('tariffComp__th', 'tariffComp__th--label')}
                />
                <TableHeaderCell
                  className={clsx('tariffComp__th', 'tariffComp__th--col')}
                >
                  <Strong>Variable</Strong>
                </TableHeaderCell>
                <TableHeaderCell
                  className={clsx('tariffComp__th', 'tariffComp__th--col')}
                >
                  <Strong>Tracker</Strong>
                </TableHeaderCell>
                <TableHeaderCell
                  className={clsx('tariffComp__th', 'tariffComp__th--col')}
                >
                  <Strong>Fixed</Strong>
                </TableHeaderCell>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell
                    className={clsx('tariffComp__td', 'tariffComp__td--label')}
                  >
                    <Strong>Price</Strong>
                  </TableCell>
                  <TableCell className="tariffComp__td">
                    <BodyText>{TARIFF_DESCRIPTIONS.variable}</BodyText>
                  </TableCell>
                  <TableCell className="tariffComp__td">
                    <BodyText>{TARIFF_DESCRIPTIONS.tracker}</BodyText>
                  </TableCell>
                  <TableCell className="tariffComp__td">
                    <BodyText>{TARIFF_DESCRIPTIONS.fixed}</BodyText>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    className={clsx('tariffComp__td', 'tariffComp__td--label')}
                  >
                    {costLabel}
                  </TableCell>
                  <TableCell className="tariffComp__td">
                    <Strong>
                      {formatCurrency({ value: variablePrice })} a year
                    </Strong>
                  </TableCell>
                  <TableCell className="tariffComp__td">
                    <Strong>
                      {trackerPrice != null
                        ? `${formatCurrency({ value: trackerPrice })} a year`
                        : 'N/A'}
                    </Strong>
                  </TableCell>
                  <TableCell className="tariffComp__td">
                    <Strong>
                      {fixedPrice != null
                        ? `${formatCurrency({ value: fixedPrice })} a year`
                        : 'N/A'}
                    </Strong>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    className={clsx('tariffComp__td', 'tariffComp__td--label')}
                  >
                    <Strong>Best for</Strong>
                  </TableCell>
                  <TableCell className="tariffComp__td">
                    <BodyText>{BEST_FOR.variable}</BodyText>
                  </TableCell>
                  <TableCell className="tariffComp__td">
                    <BodyText>{BEST_FOR.tracker}</BodyText>
                  </TableCell>
                  <TableCell className="tariffComp__td">
                    <BodyText>{BEST_FOR.fixed}</BodyText>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {/* ── Narrow layout (tabs) ── */}
          <div className="tariffComp__tabLayout">
            <Tabs
              value={activeTariff}
              onValueChange={(val) =>
                setActiveTariff(val as 'variable' | 'tracker' | 'fixed')
              }
            >
              <TabsList>
                {ENERGY_TARIFFS.map(({ key, label }) => (
                  <Tab key={key} value={key}>
                    {label}
                  </Tab>
                ))}
              </TabsList>
            </Tabs>

            <Flex
              direction="column"
              spacing="sm"
              paddingY="200"
              paddingX="300"
              className="tariffComp__contentRow"
            >
              <Strong>Price</Strong>
              <BodyText>{TARIFF_DESCRIPTIONS[activeTariff]}</BodyText>
            </Flex>

            <Flex
              direction="column"
              spacing="sm"
              paddingY="200"
              paddingX="300"
              className="tariffComp__contentRow"
            >
              {costLabel}
              <Strong>
                {tabPrice != null
                  ? `${formatCurrency({ value: tabPrice })} a year`
                  : 'N/A'}
              </Strong>
            </Flex>

            <Flex
              direction="column"
              spacing="sm"
              paddingY="200"
              paddingX="300"
              className={clsx(
                'tariffComp__contentRow',
                'tariffComp__contentRow--last'
              )}
            >
              <Strong>Best for</Strong>
              <BodyText>{BEST_FOR[activeTariff]}</BodyText>
            </Flex>
          </div>
        </Card>
      </Box>
      {activeInfo && (
        <Modal loading={false} heading={INFO_MODAL[activeInfo].heading}>
          <ModalContent>
            <BodyText>{INFO_MODAL[activeInfo].body}</BodyText>
          </ModalContent>
        </Modal>
      )}
    </ModalRoot>
  );
};

export { TariffComparison };
